import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { MyTheme } from 'Components/common/Theme'
import { numberWithComma } from 'helpers/format'
import { Col, Image, Row, Space } from 'antd'
import { appConfig } from 'config'
import { TableListPage } from 'Components/common/Table'
import { DeleteOutlined, DownloadOutlined, EditOutlined } from '@ant-design/icons'
import { ImageFallback } from 'helpers/constanst'
import { ActionButton, PrimaryButton } from 'Components/button'
import { PickUpProduct, PurchaseProduct } from 'Store/purchase/types'
import ConfirmWithChild from 'Components/modal/ConfirmWithChild'
import { AppInput } from 'Components/form'
import ProductUomMultiplier from 'Pages/Product/components/ProductUomMultiplier'

type Props = {
  products: PickUpProduct[]
  onUpdateData?: (products: PickUpProduct[]) => void
  onClickEdit?: (products: PickUpProduct) => void
  viewMode?: boolean
  onClickConfirmPickup?: (supplier_shipping_no: string) => void
  showConfirm?: boolean
  setShowConfirm?: (val: boolean) => void
}

const ProductsPickUpTable: React.FunctionComponent<Props> = props => {
  const {
    products,
    onUpdateData = () => {},
    onClickEdit = () => {},
    viewMode = false,
    onClickConfirmPickup = () => {},
    showConfirm = false,
    setShowConfirm = () => {},
  } = props
  // const [showConfirm, setShowConfirm] = useState(false)
  const [supplierShippingNo, setSupplierShippingNo] = useState('')

  useEffect(() => {
    setSupplierShippingNo('')
  }, [products])

  const renderInfo = (val: any) => {
    return <RowTile>{val}</RowTile>
  }

  const onRemoveProduct = (product: PickUpProduct) => {
    const newProduct =
      products && products.filter(item => item.purchasing_item_id != product.purchasing_item_id)
    onUpdateData(newProduct)
  }

  const renderImage = (purchase_info: PurchaseProduct) => (
    <ThumbImage src={appConfig.assetCdnEndpoint + purchase_info.product_photo} fallback={ImageFallback} />
  )

  const renderProductName = (value: PurchaseProduct) => {
    const isSameTitle = value.product_name == value.product_option_name
    return (
      <>
        <RowTile>{value.product_name}</RowTile>
        {!isSameTitle && <ProductDesc>{value.product_option_name}</ProductDesc>}
      </>
    )
  }

  const renderActionButton = (text: string, record: PickUpProduct) => {
    if (viewMode) return
    return (
      <Center>
        <Space size="small" align="center">
          <ActionButton
            text=""
            icon={<EditOutlined />}
            label="แก้ไข"
            action="edit"
            onClick={() => onClickEdit(record)}
          />
          <ActionButton
            text=""
            icon={<DeleteOutlined />}
            label="ลบ"
            action="deleted"
            onClick={() => onRemoveProduct(record)}
          />
        </Space>
      </Center>
    )
  }

  const renderQuantity = (item: number) => {
    return <RecieveProduct>{numberWithComma(item)}</RecieveProduct>
  }

  const renderRemain = (item: number, record: any) => {
    const remain = (record?.purchase_info?.amount || 0) - (record?.purchase_info?.recieved_amount || 0) - item
    return <RemainProduct>{numberWithComma(remain)}</RemainProduct>
  }

  const renderPrice = (item: PurchaseProduct) => {
    return <TextRight>{numberWithComma(item.price_per_unit, true)}</TextRight>
  }

  const renderCost = (item: string, record: PurchaseProduct) => {
    return <TextRight>{numberWithComma(record.hidden_cost, true)}</TextRight>
  }

  const renderProductUom = (uom: string, record: PickUpProduct) => {
    const uoms = record?.purchase_info?.uoms || []
    const master = uoms?.find(item => item.is_master)
    const current = uoms?.find(item => item.id === record.purchase_info?.product_uom_id)
    return (
      <span>
        <ProductUomMultiplier masterUom={master} quantity={record.received_amout || 0} currentUom={current} />
      </span>
    )
  }

  const columns = [
    {
      title: <ColTile>ลำดับ</ColTile>,
      dataIndex: 'no',
      key: 'no',
      render: (value: any, record: any, index: number) => {
        return renderInfo(index + 1)
      },
      width: '50px',
    },
    {
      title: <ColTile>รูปภาพ</ColTile>,
      dataIndex: 'purchase_info',
      key: 'product_photo',
      render: renderImage,
      width: '100px',
    },
    {
      title: <ColTile>รหัสสินค้า</ColTile>,
      dataIndex: 'purchase_info',
      key: 'product_sku',
      width: '100px',
      render: (value: PurchaseProduct) => renderInfo(value.product_sku || '-'),
    },
    {
      title: <ColTile>ยี่ห้อ</ColTile>,
      dataIndex: 'purchase_info',
      key: 'product_manufacture',
      width: '100px',
      render: (value: PurchaseProduct) => renderInfo(value.product_manufacture || '-'),
    },
    {
      title: <ColTile>รายการ</ColTile>,
      dataIndex: 'purchase_info',
      key: 'product_name',
      render: renderProductName,
      width: '200px',
    },
    {
      title: <ColTile>หน่วย</ColTile>,
      dataIndex: 'purchase_info',
      key: 'product_uom',
      width: '100px',
      render: renderProductUom,
    },
    {
      title: <ColTile>รับเข้า</ColTile>,
      dataIndex: 'received_amout',
      key: 'received_amout',
      width: '100px',
      render: renderQuantity,
    },
    {
      title: <ColTile>คงเหลือค้างรับ</ColTile>,
      dataIndex: 'received_amout',
      key: 'amount',
      width: '100px',
      render: renderRemain,
    },
    {
      title: (
        <ColTile>
          <TextRight>ราคาต่อหน่วย</TextRight>
        </ColTile>
      ),
      dataIndex: 'purchase_info',
      key: 'price_per_unit',
      width: '100px',
      render: renderPrice,
    },
    {
      title: (
        <ColTile>
          <TextRight>ต้นทุนแฝง</TextRight>
        </ColTile>
      ),
      dataIndex: 'hiden_cost',
      key: 'hiden_cost',
      render: renderCost,
      width: '100px',
    },
    {
      title: '',
      key: 'action',
      render: renderActionButton,
      width: '80px',
    },
  ]

  const confirmColumns = [
    {
      title: <ColTile>ลำดับ</ColTile>,
      dataIndex: 'no',
      key: 'no',
      render: (value: any, record: any, index: number) => {
        return renderInfo(index + 1)
      },
      width: '50px',
    },
    {
      title: <ColTile>รหัสสินค้า</ColTile>,
      dataIndex: 'purchase_info',
      key: 'product_sku',
      render: (value: PurchaseProduct) => renderInfo(value.product_sku || '-'),
    },
    {
      title: <ColTile>ยี่ห้อ</ColTile>,
      dataIndex: 'purchase_info',
      key: 'product_manufacture',
      render: (value: PurchaseProduct) => renderInfo(value.product_manufacture || '-'),
    },
    {
      title: <ColTile>รายการ</ColTile>,
      dataIndex: 'purchase_info',
      key: 'product_name',
      render: renderProductName,
      width: '200px',
    },
    {
      title: <ColTile>รับเข้า</ColTile>,
      dataIndex: 'received_amout',
      key: 'received_amout',
      render: renderQuantity,
    },
    {
      title: <ColTile>หน่วย</ColTile>,
      dataIndex: 'purchase_info',
      key: 'product_uom',
      render: (value: PurchaseProduct) => renderInfo(value.product_uom || '-'),
    },
    {
      title: (
        <ColTile>
          <TextRight>ต้นทุนแฝง</TextRight>
        </ColTile>
      ),
      dataIndex: 'hiden_cost',
      key: 'hiden_cost',
      render: renderCost,
      width: '100px',
    },
  ]

  const renderHeader = () => {
    return (
      <Row justify="space-between" gutter={[32, 32]} align="middle">
        <Col>
          <BoxTitle>
            <DownloadOutlined /> รายการรับเข้าคลัง
          </BoxTitle>
        </Col>
      </Row>
    )
  }

  const renderConfirmInfo = () => {
    return (
      <ConfirmWrapper>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Center>
              <Desc>กรุณาตรวจสอบความถูกต้องของข้อมูลก่อน &quot;ยืนยันรับเข้าคลัง&quot;</Desc>
            </Center>
          </Col>
          <Col span={24}>
            <TableListPage
              rowKey="option_id"
              tableName="product-purchase"
              columns={confirmColumns}
              datas={products}
              size={'small'}
              scrollX={500}
            />
          </Col>
          <Col md={24} xs={24}>
            <AppInput
              name="supplier_shipping_no"
              label="เลขที่ใบส่งสินค้าจากผู้จำหน่าย (ถ้ามี)"
              isRequired={false}
              isError={false}
              value={supplierShippingNo}
              onChange={(val: string) => setSupplierShippingNo(val)}
            />
          </Col>
        </Row>
      </ConfirmWrapper>
    )
  }

  const InfoModal = (
    <ConfirmWithChild
      title={'ยืนยันรับสินค้าเข้าคลัง'}
      isShow={showConfirm}
      handleOk={() => {
        setShowConfirm(false)
        onClickConfirmPickup(supplierShippingNo)
      }}
      handleCancel={() => setShowConfirm(false)}
      child={renderConfirmInfo()}
      cancelBtnTxt="ปิด"
      confirmBtnTxt="ยืนยันรับเข้าคลัง"
      width={'60%'}
    />
  )

  return (
    <BoxWrapper>
      {InfoModal}
      <Row gutter={[16, 16]}>
        <Col span={24}>
          {renderHeader()}
          <TableListPage
            rowKey="option_id"
            tableName="product-purchase"
            columns={columns}
            datas={products}
            size={'small'}
            scrollX={800}
          />
        </Col>
        {/* {!viewMode && (
          <Col span={24}>
            <Row justify="start">
              <Col>
                <PrimaryButton
                  btnType="Approve"
                  text="ยืนยันรับเข้าคลัง"
                  onClick={() => setShowConfirm(true)}
                  disabled={products?.length <= 0}
                />
              </Col>
            </Row>
          </Col>
        )} */}
      </Row>
    </BoxWrapper>
  )
}

const BoxTitle = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body3};
  font-family: ${(props: { theme: MyTheme }): string => props.theme.fontFamily.primary};
`

const BoxWrapper = styled.div`
  width: 100%;
  border-radius: 10px;
  padding: 15px 10px;
  //border: 1px solid ${(props: { theme: MyTheme }): string => props.theme.colors.whitebg};
  border: 1px solid #f0f0f0;
  margin-bottom: 10px;
  background-color:  ${(props: { theme: MyTheme }): string => props.theme.colors.white};
`

const ThumbImage = styled(Image)`
  border-radius: 5px;
  width: 70px;
  height: 70px;
  object-fit: cover;
`

const ColTile = styled.span`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
`

const RowTile = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
`

const ProductDesc = styled(RowTile)`
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.grey};
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body5};
`

const TextRight = styled.div`
  text-align: right;
`

const Center = styled.div`
  text-align: center;
`

const RemainProduct = styled.span`
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.error};
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
`

const RecieveProduct = styled.span`
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.success};
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
`

const ConfirmWrapper = styled.div`
  padding: 10px;
  text-align: left;
`

const Desc = styled.span`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.primary};
`

export default ProductsPickUpTable
